import { useId, useState, type ReactNode } from 'react';
import { I, Icon } from '../lib/icons';

export function Toggle({
  on,
  onChange,
  label,
  disabled,
}: {
  on: boolean;
  onChange?: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="toggle"
      aria-label={label}
      aria-pressed={on}
      disabled={disabled || !onChange}
      onClick={() => onChange?.(!on)}
    >
      <span />
    </button>
  );
}

export function PageHeader({ title, sub, children }: { title: string; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-head">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexGrow: 1, minWidth: 0 }}>
        <h1 className="page-title">{title}</h1>
        {sub && <div className="page-sub">{sub}</div>}
      </div>
      {children}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="empty" role="status">
      {label}
    </div>
  );
}

export function ErrorBox({ error }: { error: string | null | undefined }) {
  if (!error) return null;
  return (
    <div className="err-box" role="alert">
      {error}
    </div>
  );
}

/** Parse a numeric input value; empty string → null. */
export function numOrNull(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Password field with a show/hide control. Typing a password you cannot read is how
 * typos get saved, so every password input in the app uses this.
 */
export function PasswordField({
  value,
  onChange,
  label,
  placeholder,
  autoComplete,
  className,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  label?: ReactNode;
  placeholder?: string;
  autoComplete?: string;
  className?: string;
  disabled?: boolean;
}) {
  const [shown, setShown] = useState(false);
  const id = useId();
  return (
    <label className="field" htmlFor={id}>
      {label !== undefined && <span className="lbl">{label}</span>}
      <span className="pw-wrap">
        <input
          id={id}
          className={className ?? 'fld'}
          type={shown ? 'text' : 'password'}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="pw-toggle hov"
          // The control both describes and announces its next action.
          aria-label={shown ? 'Hide password' : 'Show password'}
          aria-pressed={shown}
          disabled={disabled}
          onClick={() => setShown((v) => !v)}
        >
          <Icon d={shown ? I.eyeOff : I.eye} size={17} />
        </button>
      </span>
    </label>
  );
}
